import { useCallback, useContext } from "react";
import { ReactReduxContext } from "react-redux";
import {
  type EventTransformerHooks,
  useEventTracking,
  type GlobalTrackingAttributes,
  type UseInstantTransformEvent,
} from "@b2bportal/core-tracking";

import type { FlightState } from "../store";
import { FlightRawEventName, type FlightCoreRawEvent } from "./events";
import { FlightEventName, type FlightCoreEvent } from "./events/amplitude";
import type { FlightAppliedFilterEvent } from "./events/amplitude/FlightAppliedFilterEvent";
import type { FlightChangedTripTypeEvent } from "./events/amplitude/FlightChangedTripTypeEvent";
import type { FlightEntryEvent } from "./events/amplitude/FlightEntryEvent";
import type { FlightSelectedEvent } from "./events/amplitude/FlightSelectedEvent";
import type { FlightViewedAirSearchEvent } from "./events/amplitude/FlightViewedAirSearchEvent";
import type { FlightViewedAmenityReviewEvent } from "./events/amplitude/FlightViewedAmenityReviewEvent";
import type { FlightViewedForecastEvent } from "./events/amplitude/FlightViewedForecastEvent";
import type { FlightViewedListEvent } from "./events/amplitude/FlightViewedListEvent";
import type { FlightViewedShopLoadingEvent } from "./events/amplitude/FlightViewedShopLoadingEvent";
import type { FlightViewedSliceEvent } from "./events/amplitude/FlightViewedSliceEvent";
import type { FlightViewedTripSummaryEvent } from "./events/amplitude/FlightViewedTripSummaryEvent";
import type { FlightAppliedFilterRawEvent } from "./events/raw/FlightAppliedFilterRawEvent";
import type { FlightChangedTripTypeRawEvent } from "./events/raw/FlightChangedTripTypeRawEvent";
import type { FlightEntryRawEvent } from "./events/raw/FlightEntryRawEvent";
import type { FlightSelectedRawEvent } from "./events/raw/FlightSelectedRawEvent";
import type { FlightViewedAirSearchRawEvent } from "./events/raw/FlightViewedAirSearchRawEvent";
import type { FlightViewedAmenityReviewRawEvent } from "./events/raw/FlightViewedAmenityReviewRawEvent";
import type { FlightViewedForecastRawEvent } from "./events/raw/FlightViewedForecastRawEvent";
import type { FlightViewedListRawEvent } from "./events/raw/FlightViewedListRawEvent";
import type { FlightViewedShopLoadingRawEvent } from "./events/raw/FlightViewedShopLoadingRawEvent";
import type { FlightViewedSliceRawEvent } from "./events/raw/FlightViewedSliceRawEvent";
import type { FlightViewedTripSummaryRawEvent } from "./events/raw/FlightViewedTripSummaryRawEvent";
import {
  getFlightSelectedFareTrackingProperties,
  getFlightSelectedTripTrackingProperties,
  getFlightShopTrackingProperties,
} from "./selectors";
import { FLIGHT_PRODUCT_TYPE } from "./types";

export const commonTrackingProperties = {
  productType: FLIGHT_PRODUCT_TYPE,
};

export const useFlightChangedTripTypeTransformer: UseInstantTransformEvent<
  FlightChangedTripTypeRawEvent,
  FlightChangedTripTypeEvent
> = () => {
  return useCallback(
    (event) => ({
      name: FlightEventName.changed_trip_type,
      properties: {
        ...commonTrackingProperties,
        ...event.properties,
      },
    }),
    []
  );
};

export const useFlightEnteredTransformer: UseInstantTransformEvent<
  FlightEntryRawEvent,
  FlightEntryEvent
> = () => {
  const providers = useProviders();

  return useCallback(
    (event) => ({
      name: FlightEventName.air_entry,
      properties: {
        ...commonTrackingProperties,
        ...getFlightShopTrackingProperties(providers.reduxState),
        ...event.properties,
      },
    }),
    [providers.reduxState]
  );
};

export const useFlightViewedFlightListLoadingTransformer: UseInstantTransformEvent<
  FlightViewedShopLoadingRawEvent,
  FlightViewedShopLoadingEvent
> = () => {
  const providers = useProviders();

  return useCallback(
    () => ({
      name: FlightEventName.viewed_flight_shop_loading,
      properties: {
        ...commonTrackingProperties,
        ...getFlightShopTrackingProperties(providers.reduxState),
      },
    }),
    [providers.reduxState]
  );
};

export const useFlightViewedFlightListTransformer: UseInstantTransformEvent<
  FlightViewedListRawEvent,
  FlightViewedListEvent
> = () => {
  const providers = useProviders();

  return useCallback(
    (event) => ({
      name: FlightEventName.viewed_flight_list,
      properties: {
        ...commonTrackingProperties,
        ...getFlightShopTrackingProperties(providers.reduxState),
        ...(event.funnel == null ? {} : { funnel: event.funnel }),
      },
    }),
    [providers.reduxState]
  );
};

export const useFlightAppliedFilterTransformer: UseInstantTransformEvent<
  FlightAppliedFilterRawEvent,
  FlightAppliedFilterEvent
> = () => {
  const providers = useProviders();

  return useCallback(
    (event) => ({
      name: FlightEventName.applied_filter,
      properties: {
        ...commonTrackingProperties,
        ...getFlightShopTrackingProperties(providers.reduxState),
        ...event.properties,
      },
    }),
    [providers.reduxState]
  );
};

export const useFlightViewedForecastTransformer: UseInstantTransformEvent<
  FlightViewedForecastRawEvent,
  FlightViewedForecastEvent
> = () => {
  const providers = useProviders();

  return useCallback(
    (event) => ({
      name: FlightEventName.viewed_forecast,
      properties: {
        ...commonTrackingProperties,
        ...getFlightShopTrackingProperties(providers.reduxState),
        ...event.properties,
      },
    }),
    [providers.reduxState]
  );
};

export const useFlightViewedSliceTransformer: UseInstantTransformEvent<
  FlightViewedSliceRawEvent,
  FlightViewedSliceEvent
> = () => {
  const providers = useProviders();

  return useCallback(
    () => ({
      name: FlightEventName.viewed_slice,
      properties: {
        ...commonTrackingProperties,
        ...getFlightShopTrackingProperties(providers.reduxState),
        ...getFlightSelectedTripTrackingProperties(providers.reduxState),
      },
    }),
    [providers.reduxState]
  );
};

export const useFlightViewedTripSummaryTransformer: UseInstantTransformEvent<
  FlightViewedTripSummaryRawEvent,
  FlightViewedTripSummaryEvent
> = () => {
  const providers = useProviders();

  return useCallback(
    () => ({
      name: FlightEventName.viewed_trip_summary,
      properties: {
        ...commonTrackingProperties,
        ...getFlightShopTrackingProperties(providers.reduxState),
        ...getFlightSelectedTripTrackingProperties(providers.reduxState),
      },
    }),
    [providers.reduxState]
  );
};

export const useFlightViewedFareDetailsTransformer: UseInstantTransformEvent<
  FlightViewedAmenityReviewRawEvent,
  FlightViewedAmenityReviewEvent
> = () => {
  const providers = useProviders();

  return useCallback(
    () => ({
      name: FlightEventName.viewed_amenity_review,
      properties: {
        ...commonTrackingProperties,
        ...getFlightShopTrackingProperties(providers.reduxState),
        ...getFlightSelectedTripTrackingProperties(providers.reduxState),
      },
    }),
    [providers.reduxState]
  );
};

export const useFlightViewedAirSearchTransformer: UseInstantTransformEvent<
  FlightViewedAirSearchRawEvent,
  FlightViewedAirSearchEvent
> = () => {
  return useCallback(
    () => ({
      name: FlightEventName.viewed_air_search,
      properties: {
        ...commonTrackingProperties,
      },
    }),
    []
  );
};

export const useFlightSelectedFlightTransformer: UseInstantTransformEvent<
  FlightSelectedRawEvent,
  FlightSelectedEvent
> = () => {
  const providers = useProviders();

  return useCallback(
    () => ({
      name: FlightEventName.selected_flight,
      properties: {
        ...commonTrackingProperties,
        ...getFlightShopTrackingProperties(providers.reduxState),
        ...getFlightSelectedTripTrackingProperties(providers.reduxState),
        ...getFlightSelectedFareTrackingProperties(providers.reduxState),
      },
    }),
    [providers.reduxState]
  );
};

export const CORE_FLIGHTS_HTS_EVENT_TRANSFORMER_HOOKS: EventTransformerHooks<
  FlightCoreRawEvent,
  FlightCoreEvent
> = {
  [FlightRawEventName.ChangedTripType]: [useFlightChangedTripTypeTransformer],
  [FlightRawEventName.EnteredFlight]: [useFlightEnteredTransformer],
  [FlightRawEventName.ViewedFlightListLoading]: [
    useFlightViewedFlightListLoadingTransformer,
  ],
  [FlightRawEventName.ViewedFlightList]: [useFlightViewedFlightListTransformer],
  [FlightRawEventName.AppliedFilter]: [useFlightAppliedFilterTransformer],
  [FlightRawEventName.ViewedForecast]: [useFlightViewedForecastTransformer],
  [FlightRawEventName.ViewedSlice]: [useFlightViewedSliceTransformer],
  [FlightRawEventName.ViewedTripSummary]: [
    useFlightViewedTripSummaryTransformer,
  ],
  [FlightRawEventName.ViewedFareDetails]: [
    useFlightViewedFareDetailsTransformer,
  ],
  [FlightRawEventName.SelectedFlight]: [useFlightSelectedFlightTransformer],
  [FlightRawEventName.ViewedAirSearch]: [useFlightViewedAirSearchTransformer],
};

function useProviders(): FlightProviders {
  const { getAllAttributes } = useEventTracking();
  const reduxContext = useContext(ReactReduxContext);

  return {
    trackingAttributes: getAllAttributes(),
    reduxState: reduxContext.store.getState(),
  };
}

interface FlightProviders {
  trackingAttributes?: GlobalTrackingAttributes;
  reduxState: FlightState;
}
